import { Observable } from 'rxjs';
import { AiBudgetApi, Plain, PlanCatalogue, Rich } from '@agendor/shared';
import AuthService from './auth-service';
import { PlainOpeningHours } from './opening-hours';

export type BusinessAuthService = Pick<AuthService, 'onSessionExpired' | 'authenticatedFetch' | 'accessToken'>;

export default class BusinessService {
    private readonly baseUrl: string;
    private readonly _authService: BusinessAuthService;

    constructor(params: { serverRootPath: string; authService: BusinessAuthService }) {
        this.baseUrl = `${params.serverRootPath}/business`;
        this._authService = params.authService;
    }

    get onInvalidToken(): Observable<void> {
        return this._authService.onSessionExpired;
    }

    async getBusiness(): Promise<BusinessDetails> {
        return await this.request<BusinessDetails>(this.baseUrl);
    }

    async getAiBudget(): Promise<AiBudgetApi.Snapshot | null> {
        return await this.request<AiBudgetApi.Snapshot | null>(`${this.baseUrl}/ai-budget`);
    }

    async purchaseCreditPack(): Promise<AiBudgetApi.PackInvoice> {
        return await this._purchaseCreditPack<AiBudgetApi.PackInvoice>({ chargeStoredCard: false });
    }

    async purchaseCreditPackWithStoredCard(): Promise<AiBudgetApi.PackCardCharge> {
        return await this._purchaseCreditPack<AiBudgetApi.PackCardCharge>({ chargeStoredCard: true });
    }

    private async _purchaseCreditPack<T extends AiBudgetApi.PurchasePackResponse>(body: {
        chargeStoredCard: boolean;
    }): Promise<T> {
        try {
            return await this.request<T>(`${this.baseUrl}/ai-budget/packs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.code === AiBudgetApi.PACK_ERROR_CARD_DECLINED) {
                    throw new PurchaseCreditPackError('card_declined');
                }
                if (error.code === AiBudgetApi.PACK_ERROR_NO_CREDIT_CARD) {
                    throw new PurchaseCreditPackError('no_credit_card');
                }
                if (error.code === AiBudgetApi.PACK_ERROR_ASAAS_CUSTOMER_NOT_FOUND) {
                    throw new PurchaseCreditPackError('asaas_customer_not_found');
                }
                if (error.code === AiBudgetApi.PACK_ERROR_AI_BUDGET_NOT_ACTIVE) {
                    throw new PurchaseCreditPackError('ai_budget_not_active');
                }
            }
            throw error;
        }
    }

    async setAutoRechargeEnabled(params: { enabled: boolean; allowChargingNow?: boolean }): Promise<void> {
        try {
            await this.request<void>(
                `${this.baseUrl}/ai-budget/auto-recharge`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(params),
                },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError && error.code === AiBudgetApi.AUTO_RECHARGE_ERROR_NO_CREDIT_CARD) {
                throw new AutoRechargeNoCreditCardError();
            }
            if (error instanceof ApiError && error.code === AiBudgetApi.AUTO_RECHARGE_ERROR_WOULD_CHARGE_NOW) {
                throw new AutoRechargeWouldChargeNowError();
            }
            throw error;
        }
    }

    async getPackHistory(month: { year: number; month: number }): Promise<AiBudgetApi.PackHistoryResponse> {
        const query = new URLSearchParams({ year: String(month.year), month: String(month.month) });
        return await this.request<AiBudgetApi.PackHistoryResponse>(
            `${this.baseUrl}/ai-budget/packs?${query.toString()}`,
        );
    }

    async getEvents(params: { date: Date }): Promise<BusinessEvent[]> {
        const query = new URLSearchParams({ date: formatDateParam(params.date) });
        return await this.request<BusinessEvent[]>(`${this.baseUrl}/events?${query.toString()}`);
    }

    async getStats(
        params: { period: '7d' } | { period: '30d' } | { period: 'month'; month: string },
    ): Promise<BusinessStats> {
        const query = new URLSearchParams({ period: params.period });
        if (params.period === 'month') {
            query.set('month', params.month);
        }
        return await this.request<BusinessStats>(`${this.baseUrl}/stats?${query.toString()}`);
    }

    async setOpeningHours(params: Record<Plain.WeekdayName, Plain.DayTimeSlice[]>): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/opening-hours`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setWorkerOpeningHours(params: { workerId: number; openingHours: PlainOpeningHours }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/workers/${params.workerId}/opening-hours`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params.openingHours),
            },
            { decode: false },
        );
    }

    async setWorkerOpeningHoursEnabled(params: { workerId: number; enabled: boolean }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/workers/${params.workerId}/opening-hours/enabled`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params.enabled),
            },
            { decode: false },
        );
    }

    async setBot(params: {
        enabled: boolean;
        overrides: Array<{ phoneNumber: string; enabled: boolean }>;
    }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/bot`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setPersonalization(params: {
        greetingMessage: string | null;
        personality: string;
        handoffEnabled: boolean;
        splitMessagesEnabled?: boolean;
    }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/personalization`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setOpenScopeEnabled(params: { enabled: boolean }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/open-scope`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setBookingRules(params: {
        minHoursNotice: number | null;
        maxDaysAhead: number | null;
        maxDurationInMinutes: number | null;
        maxConcurrent: number | null;
        checkInEnabled: boolean;
        bookingPageEnabled?: boolean;
    }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/booking`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setNotifications(params: {
        bookingReminders: BookingRemindersConfig;
        bookingConfirmation: { daysBefore: number; time: Plain.TimeOfDay } | null;
    }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/notifications`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setAsaas(params: { token: string | null; allowLosingLinkedCustomers?: boolean }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/payment/asaas`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async createAsaasSubAccount(params: AsaasAccountData & { name: string; cpfCnpj: string }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/payment/asaas/subaccount`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async getAsaasCommercialInfo(): Promise<AsaasAccountData> {
        return await this.request<AsaasAccountData>(`${this.baseUrl}/payment/asaas/commercial-info`);
    }

    async updateAsaasCommercialInfo(params: AsaasAccountData): Promise<void> {
        await this.request<void>(`${this.baseUrl}/payment/asaas/commercial-info`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
    }

    async setBookingPayment(params: {
        percentage: number | null;
        timeoutMinutes: number | null;
        transferDeadlineHours: number | null;
        method: BookingPaymentMethod;
    }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/payment/booking`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async setOwnerEmail(params: { email: string | null }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/calendar`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async requestOwnerPhoneChange(params: { phoneNumber: string }): Promise<{ verificationRequired: boolean }> {
        try {
            return await this.request<{ verificationRequired: boolean }>(`${this.baseUrl}/owner-phone/request-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.code === 'phone_number_already_in_use') {
                    throw new PhoneNumberAlreadyInUseError();
                }
                if (error.status === 429) throw new VerificationRateLimitedError();
            }
            throw error;
        }
    }

    async confirmOwnerPhoneChange(params: { phoneNumber: string; code: string }): Promise<void> {
        try {
            await this.request<void>(
                `${this.baseUrl}/owner-phone/verify-code`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(params),
                },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.code === 'phone_number_already_in_use') {
                    throw new PhoneNumberAlreadyInUseError();
                }
                if (error.code === 'invalid_code') throw new InvalidVerificationCodeError();
                if (error.status === 429) throw new VerificationRateLimitedError();
            }
            throw error;
        }
    }

    async setProfile(params: { name: string; description: string; slug: string }): Promise<{ error?: 'slug_taken' }> {
        const response = await this._authService.authenticatedFetch(`${this.baseUrl}/profile`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
        if (response.status === 409) {
            return { error: 'slug_taken' };
        }
        if (!response.ok) {
            throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }
        return {};
    }

    async getClients(): Promise<Client[]> {
        return await this.request<Client[]>(`${this.baseUrl}/clients`);
    }

    async createBlockedTime(params: {
        title: string;
        date: { year: number; month: number; day: number };
        start: { hour: number; minute: number };
        end: { hour: number; minute: number };
        workerId: number | null;
        recurrence: { intervalDays: number; endDate: { year: number; month: number; day: number } | null } | null;
        confirmationId: string | null;
    }): Promise<
        | { outcome: 'created'; blockedTime: BlockedEvent; failedCancellations: OverlappingBooking[] }
        | {
              outcome: 'overlaps_bookings';
              bookings: OverlappingBooking[];
              rangeCappedAt: SimpleDatePlain | null;
              confirmationId: string;
          }
    > {
        try {
            const response = await this.request<BlockedEvent & { failedCancellations?: OverlappingBooking[] }>(
                `${this.baseUrl}/blocked-time`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(params),
                },
            );
            const { failedCancellations, ...blockedTime } = response;
            return { outcome: 'created', blockedTime, failedCancellations: failedCancellations ?? [] };
        } catch (error) {
            if (error instanceof ApiError && error.code === 'overlaps_bookings') {
                return {
                    outcome: 'overlaps_bookings',
                    bookings: (error.data?.bookings ?? []) as OverlappingBooking[],
                    rangeCappedAt: (error.data?.rangeCappedAt ?? null) as SimpleDatePlain | null,
                    confirmationId: error.data?.confirmationId as string,
                };
            }
            if (error instanceof ApiError && error.code === 'duplicate_blocked_time') {
                throw new DuplicateBlockedTimeError();
            }
            if (error instanceof ApiError && error.code === 'unavailable_time') {
                throw new UnavailableTimeError();
            }
            throw error;
        }
    }

    async createClient(params: { phoneNumber: string; name: string }): Promise<Client> {
        try {
            return await this.request<Client>(`${this.baseUrl}/clients`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
        } catch (error) {
            if (error instanceof ApiError && error.code === 'client_already_exists') {
                throw new ClientAlreadyExistsError(error.data.client);
            }
            throw error;
        }
    }

    async setClientName(params: { clientId: number; name: string }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/clients/${params.clientId}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: params.name }),
            },
            { decode: false },
        );
    }

    async createService(params: {
        name: string;
        description: string;
        durationInMinutes: number;
        priceInCents: number;
        /**
         * Omit when the business has no intake plan (the server omits the
         * field from GET responses anyway). null = no form bound; positive
         * integer = bind to that form.
         */
        intakeId?: number | null;
    }): Promise<BusinessServiceItem> {
        return await this.request<BusinessServiceItem>(`${this.baseUrl}/services`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
    }

    async updateService(params: {
        serviceId: number;
        name: string;
        description: string;
        durationInMinutes: number;
        priceInCents: number;
        /**
         * Omit when the business has no intake plan. null = unbind; positive
         * integer = bind to that form.
         */
        intakeId?: number | null;
    }): Promise<void> {
        const { serviceId, ...body } = params;
        await this.request<void>(
            `${this.baseUrl}/services/${serviceId}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            },
            { decode: false },
        );
    }

    async deleteService(params: { serviceId: number }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/services/${params.serviceId}`,
            {
                method: 'DELETE',
            },
            { decode: false },
        );
    }

    async createWorker(params: { name: string; title: string; servicesIds: number[] }): Promise<BusinessWorker> {
        return await this.request<BusinessWorker>(`${this.baseUrl}/workers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
    }

    async updateWorker(params: {
        workerId: number;
        name: string;
        title: string;
        servicesIds: number[];
        phoneNumber: string | null;
        email: string | null;
    }): Promise<void> {
        const { workerId, ...body } = params;
        try {
            await this.request<void>(
                `${this.baseUrl}/workers/${workerId}`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError && error.code === 'phone_number_already_in_use') {
                throw new PhoneNumberAlreadyInUseError();
            }
            throw error;
        }
    }

    async deleteWorker(params: { workerId: number }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/workers/${params.workerId}`,
            {
                method: 'DELETE',
            },
            { decode: false },
        );
    }

    async setWorkerActive(params: { workerId: number; isActive: boolean }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/workers/${params.workerId}/is-active`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params.isActive),
            },
            { decode: false },
        );
    }

    async createBooking(params: {
        clientId: number;
        workerId: number;
        servicesIds: number[];
        date: { year: number; month: number; day: number };
        time: { hour: number; minute: number };
        notes: string;
        recurrence: { intervalDays: number; endDate: { year: number; month: number; day: number } | null } | null;
        isLinkedToSubscription?: boolean;
    }): Promise<BookingEvent> {
        try {
            return await this.request<BookingEvent>(`${this.baseUrl}/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
        } catch (error) {
            if (error instanceof ApiError && error.code === 'unavailable_time') {
                throw new UnavailableTimeError();
            }
            throw error;
        }
    }

    async deleteBooking(params: {
        bookingId: string;
        referenceDate: Date | null;
        scope?: 'single' | 'all';
    }): Promise<void> {
        const queryParts: string[] = [];
        if (params.referenceDate) queryParts.push(`referenceDate=${formatDateParam(params.referenceDate)}`);
        if (params.scope) queryParts.push(`scope=${params.scope}`);
        const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
        try {
            await this.request<void>(
                `${this.baseUrl}/bookings/${params.bookingId}${query}`,
                {
                    method: 'DELETE',
                },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError && error.code === 'payment_just_confirmed') {
                throw new PaymentJustConfirmedError();
            }
            throw error;
        }
    }

    async checkInBooking(params: { bookingId: string; occurrenceDate: Plain.SimpleDate }): Promise<void> {
        try {
            await this.request<void>(
                `${this.baseUrl}/bookings/${params.bookingId}/check-in`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ occurrenceDate: params.occurrenceDate }),
                },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError && error.code === 'future_occurrence') {
                throw new CheckInFutureOccurrenceError();
            }
            throw error;
        }
    }

    async getBookingIntake(bookingId: string): Promise<BookingIntake[]> {
        const body = await this.request<{ intakes: BookingIntake[] }>(`${this.baseUrl}/bookings/${bookingId}/intake`);
        return body.intakes;
    }

    async deleteBlockedTime(params: {
        eventId: string;
        referenceDate: Date | null;
        scope?: 'single' | 'all';
    }): Promise<void> {
        const queryParts: string[] = [];
        if (params.referenceDate) queryParts.push(`referenceDate=${formatDateParam(params.referenceDate)}`);
        if (params.scope) queryParts.push(`scope=${params.scope}`);
        const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
        await this.request<void>(
            `${this.baseUrl}/blocked-time/${params.eventId}${query}`,
            {
                method: 'DELETE',
            },
            { decode: false },
        );
    }

    async authorizeWhatsApp(params: {
        code: string;
        session: { phoneNumberId: string } | null;
    }): Promise<{ managerUrl: string; billingUrl: string }> {
        return await this.request<{ managerUrl: string; billingUrl: string }>(`${this.baseUrl}/whatsapp`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
    }

    async reportWhatsAppConnectionFailure(params: { metaEvent: Record<string, unknown> | null }): Promise<void> {
        await this.request(
            `${this.baseUrl}/whatsapp/connection-failures`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            },
            { decode: false },
        );
    }

    async disconnectWhatsApp(params: { testMode: boolean }): Promise<void> {
        await this.request(
            `${this.baseUrl}/whatsapp?testMode=${params.testMode}`,
            {
                method: 'DELETE',
            },
            { decode: false },
        );
    }

    async approveWhatsAppConnector(params: { userCode: string; acceptTerms: boolean }): Promise<void> {
        const { userCode, acceptTerms } = params;
        try {
            await this.request<void>(
                `${this.baseUrl}/whatsapp-connector/approve`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(acceptTerms ? { userCode, acceptTerms: true } : { userCode }),
                },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.code === 'invalid_code') throw new ConnectorCodeExpiredError();
                if (error.code === 'terms_not_accepted') throw new ConnectorTermsNotAcceptedError();
                if (error.status === 409) throw new WhatsAppAlreadyConnectedError();
                if (error.status === 404) throw new WhatsAppUnavailableError();
                if (error.status === 429) throw new ConnectorApprovalRateLimitedError();
            }
            throw error;
        }
    }

    async acceptWhatsAppConnectorTerms(): Promise<void> {
        try {
            await this.request<void>(
                `${this.baseUrl}/whatsapp-connector/terms-acceptance`,
                { method: 'POST' },
                { decode: false },
            );
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) throw new WhatsAppUnavailableError();
            throw error;
        }
    }

    /** Resolves when no computer is paired anymore, including when it was already removed. */
    async disconnectWhatsAppConnector(): Promise<void> {
        try {
            await this.request<void>(`${this.baseUrl}/whatsapp-connector`, { method: 'DELETE' }, { decode: false });
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) return;
            throw error;
        }
    }

    createTestModeWebSocket(): WebSocket {
        const url = new URL('/test-mode', this.baseUrl);
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        url.searchParams.set('authorization', `Bearer ${this._authService.accessToken}`);
        return new WebSocket(url.toString());
    }

    async simulatePlanTransition(params: { type: PlanType }): Promise<SimulationResult> {
        const response = await this._authService.authenticatedFetch(`${this.baseUrl}/subscription/simulate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });

        if (response.status === 409) {
            const body = await response.json().catch(() => ({}));
            if (body.code === 'pending_transition_exists') {
                throw new PendingTransitionExistsError();
            }
        }

        if (!response.ok) {
            throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    }

    async changePlan(plan: { type: PlanType }, params?: { eventId?: string }): Promise<TransitionResult> {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (params?.eventId) {
            headers['X-Event-ID'] = params.eventId;
        }
        const response = await this._authService.authenticatedFetch(`${this.baseUrl}/subscription`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(plan),
        });

        if (response.status === 409) {
            const body = await response.json().catch(() => ({}));
            if (body.code === 'pending_transition_exists') {
                throw new PendingTransitionExistsError();
            }
        }

        if (!response.ok) {
            throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    }

    async dismissAutoMessagesHint(): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/whatsapp-auto-messages-hint/dismiss`,
            {
                method: 'POST',
            },
            { decode: false },
        );
    }

    async clearWhatsAppBillingBlock(): Promise<void> {
        await this.request<void>(`${this.baseUrl}/whatsapp/billing-block`, { method: 'DELETE' }, { decode: false });
    }

    async cancelPendingTransition(): Promise<void> {
        const response = await this._authService.authenticatedFetch(`${this.baseUrl}/subscription/pending`, {
            method: 'DELETE',
        });

        if (!response.ok && response.status !== 404) {
            throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }
    }

    async createClientSubscription(params: {
        clientId: number;
        priceInCents: number;
        billingDay: number;
        cpfCnpj?: string;
    }): Promise<{ subscriptionId: number; invoiceUrl: string }> {
        const { clientId, ...body } = params;
        return await this.request<{ subscriptionId: number; invoiceUrl: string }>(
            `${this.baseUrl}/clients/${clientId}/subscription`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            },
        );
    }

    async cancelClientSubscription(clientId: number): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/clients/${clientId}/subscription`,
            {
                method: 'DELETE',
            },
            { decode: false },
        );
    }

    async removeCreditCard(): Promise<void> {
        const response = await this._authService.authenticatedFetch(`${this.baseUrl}/credit-card`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            throw new Error(`Request failed: ${response.status} ${response.statusText}`);
        }
    }

    // Intakes (multi-intake CRUD + per-service binding)

    async listIntakes(): Promise<Intake[]> {
        const body = await this.request<{ intakes: Intake[] }>(`${this.baseUrl}/intakes`);
        return body.intakes;
    }

    async createIntake(params: { name: string; questions: IntakeQuestionDraft[] }): Promise<Intake> {
        try {
            return await this.request<Intake>(`${this.baseUrl}/intakes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 409) {
                throw new DuplicateIntakeNameError();
            }
            throw error;
        }
    }

    async updateIntake(intakeId: number, params: { name: string; questions: IntakeQuestionDraft[] }): Promise<Intake> {
        try {
            return await this.request<Intake>(`${this.baseUrl}/intakes/${intakeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 409) {
                throw new DuplicateIntakeNameError();
            }
            throw error;
        }
    }

    async deleteIntake(intakeId: number): Promise<void> {
        await this.request<void>(`${this.baseUrl}/intakes/${intakeId}`, { method: 'DELETE' }, { decode: false });
    }

    // Knowledge (open-scope businesses only)

    async listKnowledge(): Promise<KnowledgeEntry[]> {
        return await this.request<KnowledgeEntry[]>(`${this.baseUrl}/knowledge`);
    }

    async createKnowledge(params: { title: string; summary: string | null; content: string }): Promise<KnowledgeEntry> {
        return await this.request<KnowledgeEntry>(`${this.baseUrl}/knowledge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
    }

    async draftKnowledge(params: {
        content: string;
        knowledgeId: number | null;
    }): Promise<{ title: string; summary: string }> {
        try {
            return await this.request<{ title: string; summary: string }>(`${this.baseUrl}/knowledge/draft`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 402) throw new CreditsDepletedError();
            throw error;
        }
    }

    async updateKnowledge(params: {
        knowledgeId: number;
        title: string;
        summary: string | null;
        content: string;
    }): Promise<KnowledgeEntry> {
        const { knowledgeId, ...body } = params;
        return await this.request<KnowledgeEntry>(`${this.baseUrl}/knowledge/${knowledgeId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    }

    async deleteKnowledge(params: { knowledgeId: number }): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/knowledge/${params.knowledgeId}`,
            { method: 'DELETE' },
            { decode: false },
        );
    }

    async getTemplateMappings(): Promise<TemplateMappingsResponse> {
        return await this.request<TemplateMappingsResponse>(`${this.baseUrl}/template-mappings`);
    }

    async setTemplateMappings(mappings: Array<{ action: string; templateName: string }>): Promise<void> {
        await this.request<void>(
            `${this.baseUrl}/template-mappings`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mappings }),
            },
            { decode: false },
        );
    }

    private async request<T>(url: string, init: RequestInit = {}, params?: { decode: boolean }): Promise<T> {
        const shouldDecode = params?.decode ?? true;

        const response = await this._authService.authenticatedFetch(url, init);

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new ApiError(response.status, data?.code ?? null, data?.errors, data);
        }

        if (!shouldDecode) {
            return undefined as T;
        }

        return await response.json();
    }
}

class ApiError extends Error {
    constructor(
        public status: number,
        public code: string | null,
        public errors?: { code: string; description: string }[],
        public data?: any,
    ) {
        super(`Request failed: ${status}`);
    }
}

export class UnavailableTimeError extends Error {}

export class DuplicateBlockedTimeError extends Error {}
export class ConnectorCodeExpiredError extends Error {}
export class WhatsAppAlreadyConnectedError extends Error {}
export class WhatsAppUnavailableError extends Error {}
export class ConnectorTermsNotAcceptedError extends Error {}
export class ConnectorApprovalRateLimitedError extends Error {}
export class PendingTransitionExistsError extends Error {}
export class PhoneNumberAlreadyInUseError extends Error {}
export class InvalidVerificationCodeError extends Error {}
export class VerificationRateLimitedError extends Error {}
export class CreditsDepletedError extends Error {}
export class CheckInFutureOccurrenceError extends Error {}
export class PaymentJustConfirmedError extends Error {}
export class DuplicateIntakeNameError extends Error {}
export class ClientAlreadyExistsError extends Error {
    constructor(public existingClient: { id: number; name: string | null }) {
        super('Client already exists');
    }
}

export type PurchaseCreditPackErrorCode =
    | 'asaas_customer_not_found'
    | 'ai_budget_not_active'
    | 'card_declined'
    | 'no_credit_card';
export class PurchaseCreditPackError extends Error {
    constructor(public code: PurchaseCreditPackErrorCode) {
        super(`Purchase credit pack failed: ${code}`);
    }
}

export class AutoRechargeNoCreditCardError extends Error {}

export class AutoRechargeWouldChargeNowError extends Error {}

export type PlanType = PlanCatalogue.PlanId;

export type BookingReminder =
    | { kind: 'minutesBefore'; minutes: number }
    | { kind: 'daysBefore'; daysBefore: number; time: Plain.TimeOfDay };

export type BookingRemindersConfig = {
    enabled: boolean;
    items: BookingReminder[];
};

export const MAX_BOOKING_REMINDERS = 3;

export type SimulationResult =
    | { type: 'upgrade_immediate'; newPriceInCents: number }
    | { type: 'upgrade_pending'; newPriceInCents: number; proRataAmountInCents: number }
    | { type: 'upgrade_as_trial'; newPriceInCents: number; trialEndsAt: Plain.SimpleDate }
    | { type: 'trial_plan_changed'; newPriceInCents: number; trialEndsAt: Plain.SimpleDate }
    | { type: 'downgrade_scheduled'; newPriceInCents: number; effectiveDate: Plain.SimpleDate }
    | { type: 'same_plan' };

type TransitionResult =
    | { type: 'upgrade_immediate' }
    | { type: 'upgrade_pending'; proRataInvoiceUrl: string }
    | { type: 'upgrade_as_trial'; trialEndsAt: Plain.SimpleDate }
    | { type: 'trial_plan_changed'; newPriceInCents: number; trialEndsAt: Plain.SimpleDate }
    | { type: 'downgrade_scheduled'; effectiveDate: Plain.SimpleDate }
    | { type: 'same_plan' };

function formatDateParam(date: Date): string {
    return new Rich.SimpleDate(date).toISOString();
}

export type WhatsAppConnectorStatus = {
    online: boolean;
    whatsapp: 'pairing' | 'connecting' | 'connected' | 'logged_out' | null;
    lastSeenAt: string | null;
    // Why the number the computer last linked was refused, until it links one the business takes.
    refusal: 'phone_number_in_use' | null;
};

export type BookingPaymentMethod = 'pix' | 'card' | 'boleto' | 'any';

export type BusinessDetails = {
    id: number;
    name: string | null;
    slug: string;
    description: string;
    // Present only on a subscription sold before workers became unlimited: the cap on
    // active workers, which lasts until the owner moves to a current plan.
    legacyWorkersLimit?: number;
    whatsapp:
        | { type: 'test_mode'; phoneNumber: string; expiresAt: number | null }
        | {
              type: 'cloud_api';
              phoneNumber: string;
              invalidated: boolean;
              managerUrl: string;
              billingUrl: string;
              // Present while Meta refuses the assistant's replies for lack of a payment method.
              billingBlocked?: true;
          }
        | { type: 'connector'; phoneNumber: string; invalidated: boolean }
        | null;
    // Present when the business can connect through the desktop connector; null until a computer is paired.
    whatsappConnector?: WhatsAppConnectorStatus | null;
    // Present when the business can connect through the desktop connector: whether the owner accepted
    // the risks and terms of connecting through the computer.
    connectorTermsAccepted?: boolean;
    owner: { phoneNumber: string; email: string | null };
    calendarUrl: string | null;
    chatUrl: string | null;
    bookingReminders: BookingRemindersConfig;
    bookingConfirmation: { daysBefore: number; time: Plain.TimeOfDay } | null;
    checkInEnabled: boolean;
    // Absent for businesses whose clients are not served over WhatsApp.
    bookingPageEnabled?: boolean;
    // Payments and client subscriptions are absent without paid access.
    bookingPayment?: {
        percentage: number;
        timeoutMinutes: number | null;
        transferDeadlineHours: number | null;
        method: BookingPaymentMethod;
    } | null;
    hasAsaasToken?: boolean;
    asaasSubaccount?: { expirationDate: string | null } | null;
    booking: {
        minHoursNotice: number | null;
        maxDaysAhead: number | null;
        maxDurationInMinutes: number | null;
        maxConcurrent: number | null;
    };
    bot: {
        enabled: boolean;
        handoffEnabled: boolean;
        // Absent for businesses connected through the desktop connector.
        splitMessagesEnabled?: boolean;
        greetingMessage: string | null;
        personality: string;
        overrides: Array<{ phoneNumber: string; enabled: boolean }>;
    };
    openingHours: Record<Plain.WeekdayName, Plain.DayTimeSlice[]>;
    subscription: {
        planType: PlanType;
        // The price this subscription is billed at; a grandfathered row keeps the price it
        // was created with even after the catalogue price moves.
        monthlyPriceInCents: number;
        invoiceUrl: string | null;
        nextBillingDate: Plain.SimpleDate;
        isTrial: boolean;
        isTrialEligible: boolean;
        startDate: Plain.SimpleDate;
        creditCard: { last4: string; brand: string } | null;
        pendingTransition: {
            type: 'pending_upgrade' | 'scheduled_downgrade';
            planType: PlanType;
            invoiceUrl?: string | null;
            effectiveDate?: Plain.SimpleDate;
        } | null;
        hasAiBudget: boolean;
    } | null;
    isAutoMessagesHintDismissed: boolean;
    // Present on a business granted WhatsApp by hand, whatever its plan.
    whatsAppComped?: true;
    clientSubscriptionsEnabled?: boolean;
    // Present only when the business is credit-metered; absent means the
    // open-scope setting is unavailable and must not be shown.
    openScope?: { enabled: boolean; knowledgeCount: number };
    // Same gate as openScope: absent means the auto-recharge toggle does not exist.
    autoRecharge?: AiBudgetApi.AutoRechargeSettings;
    intakes?: Intake[];
    workers: BusinessWorker[];
    services: BusinessServiceItem[];
    userLevel: 'owner' | 'worker';
    workerId: number | null;
};

export type BusinessWorker = {
    id: number;
    calendarUrl: string;
    name: string;
    title: string;
    phoneNumber: string | null;
    email: string | null;
    servicesIds: number[];
    isActive: boolean;
    // null means the worker follows the business's opening hours.
    openingHours: PlainOpeningHours | null;
};

export type BusinessServiceItem = {
    id: number;
    name: string;
    description: string;
    priceInCents: number;
    durationInMinutes: number;
    intakeId?: number | null;
};

export type IntakeQuestion = {
    id: number;
    questionText: string;
    type: 'text' | 'textarea' | 'boolean' | 'enum';
    options: string[] | null;
    required: boolean;
    position: number;
};

export type IntakeQuestionDraft = {
    id: number | null;
    questionText: string;
    type: 'text' | 'textarea' | 'boolean' | 'enum';
    options: string[] | null;
    required: boolean;
};

export type Intake = {
    id: number;
    name: string;
    questions: IntakeQuestion[];
};

export type KnowledgeEntry = {
    id: number;
    title: string;
    summary: string;
    content: string;
};

export type BusinessStats = {
    startDate: Plain.SimpleDate;
    endDate: Plain.SimpleDate;
    totalBookings: number;
    cancelledBookings: number;
    confirmedBookings?: number;
    checkedInBookings?: number;
    totalRevenueInCents: number;
    totalClients: number;
    newClients: number;
    servicesCounts: { serviceId: number; count: number }[];
    workerStats: {
        workerId: number;
        bookings: number;
        cancelled: number;
        confirmed?: number;
        checkedIn?: number;
        revenueInCents: number;
    }[];
};

export type Client = {
    id: number;
    name: string | null;
    phoneNumber: string | null;
    email: string | null;
    activeSubscription: {
        id: number;
        priceInCents: number;
        billingDay: number;
        status: 'pending_payment' | 'active' | 'overdue';
        creditCard: { last4: string; brand: string } | null;
    } | null;
    requiresCpf: boolean;
};

type Recurrence = { intervalDays: number | null; endDate: string | null };

type BookingEvent = {
    type: 'booking';
    id: string;
    start: Plain.DateTime;
    end: Plain.DateTime;
    worker: { id: number; name: string | null };
    client: { id: number; phoneNumber: string | null; email: string | null; name: string | null };
    services: { id: number; name: string; durationInMinutes: number }[];
    notes: string;
    recurrence: Recurrence;
    confirmedAt: Plain.DateTime | null;
    checkedInAt: Plain.DateTime | null;
    cancelledAt: Plain.DateTime | null;
    payment: { paidAt: Plain.DateTime | null } | null;
    isLinkedToSubscription: boolean;
    intakeStatus?: 'pending' | 'completed' | null;
};

type BlockedEvent = {
    type: 'blocked';
    id: string;
    title: string;
    start: Plain.DateTime;
    end: Plain.DateTime;
    worker: { id: number; name: string | null } | null;
    recurrence: Recurrence;
};

export type BusinessEvent = BookingEvent | BlockedEvent;

type SimpleDatePlain = { year: number; month: number; day: number };

export type OverlappingBooking = {
    bookingId: string;
    clientId: number;
    clientName: string | null;
    workerId: number;
    servicesIds: number[];
    date: SimpleDatePlain;
    time: { start: { hour: number; minute: number }; end: { hour: number; minute: number } };
    isRecurring: boolean;
    totalAffectedOccurrences: number;
};

export type BookingIntakeQuestion = {
    id: number;
    questionText: string;
    type: 'text' | 'textarea' | 'boolean' | 'enum';
    options: string[] | null;
    required: boolean;
    position: number;
};

export type BookingIntakeAnswer = {
    questionId: number;
    answerText: string;
};

/**
 * Booking-scoped view of an intake — id/name + this booking's status + the
 * client's submitted answers (empty when status === 'pending'). The owner's
 * booking-details modal iterates and renders one section per entry.
 */
export type BookingIntake = {
    id: number;
    name: string;
    status: 'pending' | 'completed' | null;
    questions: BookingIntakeQuestion[];
    answers: BookingIntakeAnswer[];
};

export type ClientSubscriptionStatus = 'pending_payment' | 'active' | 'overdue' | 'cancelled';

export type ClientSubscription = {
    id: number;
    clientId: number;
    priceInCents: number;
    billingDay: number;
    creditCardLast4: string | null;
    creditCardBrand: string | null;
    status: ClientSubscriptionStatus;
    startedAt: string | null;
    cancelledAt: string | null;
};

/** A paid plan, or a comp standing in for one: what everything that reaches the clients needs. */
export function hasPaidAccess(
    business: Pick<BusinessDetails, 'whatsAppComped'> & {
        subscription: Pick<NonNullable<BusinessDetails['subscription']>, 'planType'> | null;
    },
): boolean {
    if (business.subscription && PlanCatalogue.isPaid(business.subscription.planType)) return true;
    return business.whatsAppComped === true;
}

/** The desktop connector is turned on per business, on top of a plan that includes WhatsApp. */
export function hasConnectorAccess(business: Pick<BusinessDetails, 'connectorTermsAccepted'>): boolean {
    return business.connectorTermsAccepted !== undefined;
}

/**
 * Whether a background refresh brought anything new. An online connector's `lastSeenAt` moves on
 * every read and means nothing until it goes offline, so it doesn't count.
 */
export function isSameBusinessDetails<T extends Pick<BusinessDetails, 'whatsappConnector'>>(
    previous: T,
    next: T,
): boolean {
    return JSON.stringify(withoutLiveLastSeen(previous)) === JSON.stringify(withoutLiveLastSeen(next));
}

function withoutLiveLastSeen<T extends Pick<BusinessDetails, 'whatsappConnector'>>(business: T): T {
    if (!business.whatsappConnector?.online) return business;
    return { ...business, whatsappConnector: { ...business.whatsappConnector, lastSeenAt: null } };
}

export type AsaasAccountData = {
    email: string;
    mobilePhone: string;
    postalCode: string;
    address: string;
    addressNumber: string;
    province: string;
    incomeValue: number;
    birthDate: string;
};

export type TemplateMappingAction = {
    action: string;
    label: string;
    description: string;
    templateName: string;
    requiredParameters: string[];
    parameterExamples: Record<string, string>;
};

export type TemplateMappingsAvailableTemplate = {
    name: string;
    status: string;
    parameters: string[];
    text: string;
};

export type TemplateMappingsResponse = {
    actions: TemplateMappingAction[];
    availableTemplates: TemplateMappingsAvailableTemplate[];
    managerUrl: string;
};

export type AsaasCommercialInfoStatus = 'ok' | 'expiring_soon' | 'expired';

const EXPIRING_SOON_DAYS = 40;

export function deriveCommercialInfoStatus(expirationDate: string | null): AsaasCommercialInfoStatus {
    if (!expirationDate) return 'ok';
    const expiration = Rich.SimpleDate.fromISOString(expirationDate);
    const today = new Rich.SimpleDate(new Date());
    const daysUntil = today.daysUntil(expiration);
    if (daysUntil <= 0) return 'expired';
    if (daysUntil <= EXPIRING_SOON_DAYS) return 'expiring_soon';
    return 'ok';
}
